/**
 * PsicoSalud — Aplicación de escalas psicométricas (Módulo 4).
 *
 * Flujo: se elige la escala, se inicia la respuesta, el profesional la
 * administra durante la sesión y al enviarla el backend calcula el
 * puntaje y la vincula al historial del paciente.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DynamicForm from '@/components/DynamicForm';
import type { Answers } from '@/components/DynamicForm';
import { clinicalRecordsApi, formsApi, patientsApi } from '@/api';
import { ApiError } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import type { ClinicalRecord, FormResponseResult, FormTemplate, Patient } from '@/types';

const SEVERITY_BADGE = (severity: string): string => {
  const normalized = severity.toLowerCase();
  // El orden importa y era el defecto M10: «moderadamente severa» contiene
  // «severa», de modo que evaluar «severa» primero la pintaba con el color
  // de la categoría más grave. Cuatro de los cinco rangos del PHQ-9 se
  // distinguen por el color; confundir dos de ellos vacía la señal.
  if (normalized.includes('moderadamente')) return 'badge-warning';
  if (normalized.includes('severa')) return 'badge-critical';
  if (normalized.includes('moderada')) return 'badge-warning';
  if (normalized.includes('leve')) return 'badge-info';
  return 'badge-success';
};

export default function FormsPage() {
  const { t } = useTranslation();
  const { patientId = '' } = useParams();
  const { user } = useAuth();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [selectedId, setSelectedId] = useState('');
  // Consulta a la que se vincula el resultado. Sin esto la columna quedaba
  // siempre nula y las escalas no se podían leer junto a la sesión en que
  // se aplicaron (defecto M7).
  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [recordId, setRecordId] = useState('');
  const [history, setHistory] = useState<FormResponseResult[]>([]);
  const [result, setResult] = useState<FormResponseResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, templateList, responses, history] = await Promise.all([
        patientsApi.get(patientId),
        formsApi.listTemplates(),
        formsApi.patientResponses(patientId),
        clinicalRecordsApi.patientHistory(patientId),
      ]);
      setPatient(detail);
      setTemplates(templateList);
      setHistory(responses);

      // La escala se aplica durante la sesión, así que la consulta más
      // reciente es la vinculación correcta salvo indicación del profesional.
      const ordenados = [...history].sort((a, b) =>
        b.session_date.localeCompare(a.session_date),
      );
      setRecords(ordenados);
      setRecordId((actual) => actual || ordenados[0]?.id || '');
    } catch {
      setFeedback({ type: 'error', text: t('errors.network') });
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (answers: Answers) => {
    setSubmitting(true);
    setFeedback(null);
    try {
      // La respuesta se crea al enviarla: iniciarla al abrir la pantalla
      // dejaría registros IN_PROGRESS huérfanos cada vez que el
      // profesional cambia de escala o abandona la vista.
      const started = await formsApi.startResponse({
        template_id: selectedId,
        patient_id: patientId,
        clinical_record_id: recordId || undefined,
      });
      const completed = await formsApi.completeResponse(started.id, answers);
      setResult(completed);
      setFeedback({ type: 'success', text: t('forms.completed') });
      setHistory(await formsApi.patientResponses(patientId));
    } catch (error) {
      setFeedback({
        type: 'error',
        text: error instanceof ApiError ? error.message : t('errors.generic'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSeed = async () => {
    try {
      await formsApi.seedDefaults();
      await load();
    } catch (error) {
      setFeedback({
        type: 'error',
        text: error instanceof ApiError ? error.message : t('errors.generic'),
      });
    }
  };

  if (loading) return <div className="empty-state">{t('common.loading')}</div>;

  const selected = templates.find((template) => template.id === selectedId);

  return (
    <>
      <header className="page-header with-flex">
        <div>
          <span className="page-eyebrow">{t('forms.title')}</span>
          <h1>{t('forms.title')}</h1>
          <p className="page-descripcion">
            {patient ? (
              <>
                <Link
                  to={`/pacientes/${patientId}`}
                  style={{ color: 'var(--principal)', fontWeight: 'var(--peso-medio)' }}
                >
                  {patient.full_name}
                </Link>
                <span className="t-numerico"> · {patient.document_id}</span>
              </>
            ) : (
              <span>{t('common.loading')}</span>
            )}
          </p>
        </div>
        <div className="page-acciones">
          <Link
            to={`/pacientes/${patientId}/indicadores`}
            className="is-button is-secondary is-pequeno"
          >
            {t('analytics.title')} →
          </Link>
        </div>
      </header>

      {feedback && (
        <div
          className={`notice notice-${feedback.type}`}
          role="status"
          data-cy={`forms-feedback-${feedback.type}`}
        >
          {feedback.text}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-eyebrow">{t('forms.selectScale')}</span>
            <h2>{t('forms.selectScale')}</h2>
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="scale">{t('forms.selectScale')}</label>
            <select
              id="scale"
              value={selectedId}
              onChange={(event) => {
                setSelectedId(event.target.value);
                setResult(null);
              }}
              data-cy="forms-scale-select"
            >
              <option value="">—</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.code} — {template.name}
                </option>
              ))}
            </select>
          </div>

          {records.length > 0 && (
            <div className="field">
              <label htmlFor="record">{t('forms.linkToRecord')}</label>
              <select
                id="record"
                value={recordId}
                onChange={(event) => setRecordId(event.target.value)}
                data-cy="forms-record-select"
              >
                {records.map((record) => (
                  <option key={record.id} value={record.id}>
                    {new Date(record.session_date).toLocaleDateString('es-PY')} —{' '}
                    {t(`record.types.${record.record_type}`)}
                  </option>
                ))}
              </select>
              <p className="hint">{t('forms.linkToRecordHint')}</p>
            </div>
          )}
        </div>

        {templates.length === 0 && (
          <>
            <p className="t-pequeno t-suave">{t('forms.noTemplates')}</p>
            {/* La carga inicial de escalas está restringida a ADMIN en el
                backend; ofrecerla a un psicólogo solo produciría un 403. */}
            {user?.role === 'ADMIN' && (
              <button className="secondary" onClick={() => void handleSeed()} data-cy="forms-seed">
                {t('forms.seed')}
              </button>
            )}
          </>
        )}
      </div>

      {selected && !result && (
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-eyebrow">{selected.code}</span>
              <h2>{selected.name}</h2>
            </div>
          </div>
          <DynamicForm template={selected} submitting={submitting} onSubmit={handleSubmit} />
        </div>
      )}

      {result?.scores?.alerts && result.scores.alerts.length > 0 && (
        <div
          className={
            result.scores.requires_immediate_review
              ? 'notice notice-error'
              : 'notice notice-warning'
          }
          role="alert"
          data-cy="forms-critical-alert"
        >
          <strong>
            {result.scores.requires_immediate_review
              ? t('forms.criticalAlert')
              : t('forms.incompleteScreening')}
          </strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
            {result.scores.alerts.map((alerta) => (
              <li key={alerta.scoring_key}>
                {alerta.message}
                {alerta.action && (
                  <>
                    {' '}
                    <em>
                      {t('forms.alertAction')}: {alerta.action}
                    </em>
                  </>
                )}
              </li>
            ))}
          </ul>
          <p className="t-pequeno" style={{ margin: '0.5rem 0 0' }}>
            {t('forms.criticalAlertHint')}
          </p>
        </div>
      )}

      {result?.scores && (
        <div className="card" data-cy="forms-result">
          <div className="card-header">
            <div>
              <span className="card-eyebrow">{t('forms.result')}</span>
              <h2>{t('forms.result')}</h2>
            </div>
            <span
              className={`badge ${SEVERITY_BADGE(result.scores.severity)}`}
              data-cy="forms-severity"
            >
              {result.scores.severity}
            </span>
          </div>

          <div className="stat" style={{ marginBottom: 'var(--espacio-4)' }}>
            <div className="stat-value" data-cy="forms-score">
              {result.scores.total_score}
              <span
                style={{
                  fontSize: 'var(--texto-lg)',
                  color: 'var(--tinta-tenue)',
                  fontWeight: 'var(--peso-regular)',
                  marginLeft: 'var(--espacio-1)',
                }}
              >
                /{result.scores.max_score}
              </span>
            </div>
            <div className="stat-label">{t('forms.totalScore')}</div>
          </div>

          <p>
            <strong>{t('forms.interpretation')}:</strong> {result.scores.interpretation}
          </p>
          <p>
            <strong>{t('forms.recommendedAction')}:</strong> {result.scores.recommended_action}
          </p>

          <div className="form-actions">
            <button className="secondary" onClick={() => setResult(null)}>
              {t('common.back')}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div>
            <span className="card-eyebrow">{t('forms.history')}</span>
            <h2>{t('forms.history')}</h2>
          </div>
          <span className="t-mayuscula" style={{ color: 'var(--tinta-suave)' }}>
            {history.length} aplicaciones
          </span>
        </div>

        {history.length === 0 ? (
          <p className="empty-state">{t('common.empty')}</p>
        ) : (
          <div className="table-wrapper">
            <table data-cy="forms-history">
              <thead>
                <tr>
                  <th style={{ width: '8rem' }}>{t('common.date')}</th>
                  <th style={{ width: '6rem' }}>{t('forms.scale')}</th>
                  <th style={{ width: '8rem' }}>{t('forms.totalScore')}</th>
                  <th>{t('forms.severity')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((response) => (
                  <tr key={response.id}>
                    <td className="t-numerico">
                      {response.completed_at
                        ? new Date(response.completed_at).toLocaleDateString('es-PY')
                        : '—'}
                    </td>
                    <td>
                      <span className="badge badge-neutral">{response.template_code}</span>
                    </td>
                    <td className="t-numerico">
                      {response.scores
                        ? `${response.scores.total_score}/${response.scores.max_score}`
                        : '—'}
                    </td>
                    <td>
                      {response.scores && (
                        <span
                          className={`badge ${SEVERITY_BADGE(response.scores.severity)}`}
                        >
                          {response.scores.severity}
                        </span>
                      )}
                      {response.scores?.requires_immediate_review && (
                        <span
                          className="badge badge-critical"
                          style={{ marginLeft: '0.35rem' }}
                          data-cy="forms-history-alert"
                        >
                          {t('forms.criticalAlert')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
